import { useEffect, useRef } from 'react'
import { countCalendarFilters } from './matcher'
import { type CalendarFilterState } from './types'
import {
  type CalendarLegacyFilterValues,
  calendarFiltersFromWeekView,
} from './weekViewSeed'

/**
 * Starts a month that has never had filters of its own from the week view's.
 *
 * This is a starting point rather than a link. It happens once, the month
 * writes what it takes over into its own address and storage, and nothing is
 * ever written back to the week: the two views filter apart from then on.
 *
 * Everything it reads arrives in its own time — the option lists it needs to
 * name a value, and the week's own filters, which come from a store the
 * calendar does not own. So it waits, and keeps waiting: it is only spent once
 * it has something to hand over, and until then a later render can still be
 * the one that seeds. `hasFiltersNow` closes it again the moment the month
 * holds anything, so a person who filters while the week's values are still on
 * their way is never overruled.
 */
export const useWeekViewFilterSeed = ({
  areValuesReady,
  dates,
  hasFiltersNow,
  isWanted,
  legacyIds,
  onSeed,
  values,
}: {
  /** The lists the lookup is built from have arrived. */
  areValuesReady: boolean
  /** The day each end of the week view's own live window is set to. */
  dates: Readonly<Record<string, string>>
  /** The month holds a filter already, whoever put it there. */
  hasFiltersNow: boolean
  /**
   * The month has never had filters of its own — nothing in the address, and
   * nothing remembered for this workspace.
   */
  isWanted: boolean
  /** The week view's filters, as the shared filter bar's own value ids. */
  legacyIds: readonly string[]
  onSeed: (state: CalendarFilterState) => void
  values: CalendarLegacyFilterValues
}): void => {
  const isSpent = useRef(false)
  // The listener alone is held by reference, so a caller that rebuilds it does
  // not decide when this runs.
  const listener = useRef(onSeed)
  listener.current = onSeed

  useEffect(() => {
    if (isSpent.current || !isWanted || hasFiltersNow || !areValuesReady) return

    const seeded = calendarFiltersFromWeekView(legacyIds, values, dates)
    // Nothing to hand over yet. Whatever it was waiting for may still arrive,
    // so this is not the run that spends it.
    if (countCalendarFilters(seeded) === 0) return

    isSpent.current = true
    listener.current(seeded)
  }, [areValuesReady, dates, hasFiltersNow, isWanted, legacyIds, values])
}
